import React, { useEffect, useState } from 'react';
import { FCW } from '../../types.ts';
import { SaveRegistrationContext } from '@/saveRegistration/SaveRegistrationContext.ts';
import { OnlineStatus, useOnlineStatus } from '@/saveRegistration/actions/useOnlineStatus.ts';
import { GridStatus, useGridCheck } from '@/saveRegistration/actions/useGridCheck.ts';
import {
  TransmitRegistrationStatus,
  useTransmitRegistration,
} from '@/saveRegistration/actions/useTransmitRegistration.ts';
import {
  ConfirmTransmissionStatus,
  useConfirmTransmission,
} from '@/saveRegistration/actions/useConfirmTransmittion.ts';
import { useStickyState } from '@/util/useStickyState.ts';
import { useNavigation } from '@/navigation/NavigationContex.ts';
import { useLayer } from '@/layers/useLayer.tsx';
import { useForm } from '@/form/useForm.ts';

export type SaveStep = 'idle' | 'online-check' | 'grid-check' | 'transmit-registration' | 'confirm-transmission';

export interface SaveRegistrationApi {
  activeStep: SaveStep;
  onlineStatus: OnlineStatus;
  gridStatus: GridStatus;
  onlineCheck: () => void;
  gridCheck: () => void;
  transmit: () => void;
  transmitRegistrationStatus: TransmitRegistrationStatus;
  confirmTransmission: () => void;
  confirmTransmitStatus: ConfirmTransmissionStatus;
}
/* eslint-disable react-hooks/exhaustive-deps */
export const SaveRegistrationProvider: FCW = ({ children }) => {
  const spinnerDuration = 1000; // ms show spinner for at least 1000ms for UX reasons

  const [activeStep, setActiveStep] = useState<SaveStep>('idle');
  const { showLayer } = useLayer();
  const { onlineStatus, onlineCheck: doOnlineCheck } = useOnlineStatus({ spinnerDuration });
  const { gridCheck: doGridCheck, gridStatus } = useGridCheck({ spinnerDuration });
  const { transmit: doTransmit, transmitRegistrationStatus } = useTransmitRegistration({ spinnerDuration });
  const { confirmTransmission: doConfirmTransmission, confirmTransmitStatus } = useConfirmTransmission({
    spinnerDuration,
  });
  const { getFormPayload } = useForm();
  const [completedGrids, setCompletedGrids] = useStickyState<string[]>('dkms.completed-grids', []);
  const { setSlideValid } = useNavigation();

  useEffect(() => {
    // Start Process
    onlineCheck();
  }, []);

  useEffect(() => {
    if (onlineStatus === 'online') {
      // call next step
      gridCheck();
    }

    if (onlineStatus === 'no-backend') {
      showLayer({ ref: '@onlineNoBackend' });
    }

    if (onlineStatus === 'offline') {
      // When Offline, and public device show a notification layer
      showLayer({ ref: '@networkNotAvailable' });
    }
  }, [onlineStatus]);

  useEffect(() => {
    if (gridStatus === 'usable') {
      // Next step
      transmit();
    }

    if (gridStatus === 'used') {
      // This might come up, if the checks are re-run after successfully storing a Grid Registration
      // In this case we offer to go forward:
      const gridId = getFormPayload().donorId!;
      if (completedGrids.includes(gridId)) {
        showLayer({ ref: '@gridUsedButSubmitted' });
        setTimeout(() => {
          setSlideValid(true);
        }, 500);
      } else {
        // If we did not send the registration with this grid ID from this device we recommend destruction:
        showLayer({ ref: '@gridUsed' });
      }
    }
  }, [gridStatus]);

  useEffect(() => {
    if (transmitRegistrationStatus === 'transmitted-success') {
      // Next step
      confirmTransmission();
    }

    if (transmitRegistrationStatus === 'transmitted-failure') {
      showLayer({ ref: '@transmissionFailed' });
    }

    if (transmitRegistrationStatus === 'transmitted-unknown-failure') {
      showLayer({ ref: '@transmissionFailed' });
    }
  }, [transmitRegistrationStatus]);

  useEffect(() => {
    if (confirmTransmitStatus === 'confirmation-failure') {
      showLayer({ ref: '@confirmationFailed' });
    }
  }, [confirmTransmitStatus]);

  useEffect(() => {
    // We have won, lets store the GridID as successful and validate the slide
    if (
      onlineStatus === 'online' &&
      gridStatus === 'usable' &&
      transmitRegistrationStatus === 'transmitted-success' &&
      confirmTransmitStatus === 'confirmation-success'
    ) {
      setCompletedGrids((v: string[]) => {
        const gridId = getFormPayload().donorId!;
        return [...v, gridId];
      });

      setTimeout(() => {
        setSlideValid(true);
      }, 500);
    }
  }, [onlineStatus, gridStatus, transmitRegistrationStatus, confirmTransmitStatus]);

  const onlineCheck = () => {
    setActiveStep('online-check');
    console.log('online check');
    void doOnlineCheck();
  };

  const gridCheck = () => {
    setActiveStep('grid-check');
    console.log('grid check');
    void doGridCheck();
  };

  const transmit = () => {
    setActiveStep('transmit-registration');
    console.log('transmit registration');
    void doTransmit();
  };

  const confirmTransmission = () => {
    setActiveStep('confirm-transmission');
    console.log('confirm transmission');
    void doConfirmTransmission();
  };

  return (
    <SaveRegistrationContext.Provider
      value={{
        activeStep,
        onlineStatus,
        onlineCheck,
        gridStatus,
        gridCheck,
        transmit,
        transmitRegistrationStatus,
        confirmTransmission,
        confirmTransmitStatus,
      }}
    >
      {children}
    </SaveRegistrationContext.Provider>
  );
};
/* eslint-enable react-hooks/exhaustive-deps */
